import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import useSeo from "../../hooks/useSeo";
import "./Public.css";

const pubUrl = process.env.PUBLIC_URL || "";

const serviceItems = [
  {
    title: "Privacy Fencing",
    body: "Full-height fence layouts for backyards, side yards, and property separation where privacy and clean lines matter.",
  },
  {
    title: "Fence Replacement",
    body: "Removal and replacement of aging, leaning, storm-damaged, or poorly built fencing with a better planned system.",
  },
  {
    title: "Gates & Access",
    body: "Gate placement, hardware, swing clearance, and access points planned around how the property is actually used.",
  },
  {
    title: "Repairs",
    body: "Targeted work for damaged boards, loose sections, post issues, and gates that no longer close properly.",
  },
];

const processSteps = [
  "Review the fence line, grading, access, and existing site conditions.",
  "Confirm style, materials, gate locations, and practical installation constraints.",
  "Prepare the work area, set the layout, and install with attention to alignment.",
  "Clean up the site and review the finished work before close-out.",
];

export default function Fencing() {
  useSeo({
    title: "Fencing | Crowned Roofing",
    description:
      "Fence installation, replacement, gate work, and fence repairs with practical planning and clean exterior construction.",
    ogImage: "https://www.crownedroofing.com/images/ogimg.png",
    canonical: "https://www.crownedroofing.com/fencing",
  });

  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Fencing | Crowned Roofing";
    const meta = document.querySelector("meta[name='description']");
    if (meta) {
      meta.setAttribute(
        "content",
        "Fence installation, replacement, gate work, and fence repairs with practical planning and clean exterior construction."
      );
    }
  }, []);

  return (
    <main className="publicPage fencingPage">
      <Header />

      <section className="fenceHero">
        <div className="fenceHeroMedia" aria-hidden="true">
          <img src={`${pubUrl}/images/landing/house.png`} alt="" />
        </div>
        <div className="fenceHeroInner">
          <div className="fenceHeroCopy">
            <span className="fenceEyebrow">Exterior construction</span>
            <h1>Fencing Built Around the Property</h1>
            <p>
              Fence installation and replacement planned around layout, privacy, access,
              curb appeal, and long-term durability.
            </p>
            <div className="fenceHeroActions">
              <button className="rBtn" onClick={() => navigate("/contact")}>
                REQUEST AN ESTIMATE
              </button>
              <button className="rLink fenceHeroLink" onClick={() => navigate("/portfolio")}>
                View project examples
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="fenceIntro">
        <div className="fenceIntroGrid">
          <div>
            <h2>
              <span className="rAccent" aria-hidden="true" />
              A better boundary
            </h2>
          </div>
          <div className="fenceIntroText">
            <p>
              A fence affects how a property feels every day. It controls privacy,
              defines outdoor space, protects access points, and has to look intentional
              from more than one angle.
            </p>
            <p>
              We keep the process straightforward: assess the site, define the fence type
              and layout, install cleanly, and leave the property ready to use.
            </p>
          </div>
        </div>
      </section>

      <section className="fenceServices">
        <div className="container">
          <div className="fenceSectionHead">
            <h2>Fencing Services</h2>
            <p>Clear scopes for common fence projects, from full replacement to targeted repair work.</p>
          </div>

          <div className="fenceServiceGrid">
            {serviceItems.map((item) => (
              <article className="fenceServiceCard" key={item.title}>
                <div className="fenceCardMark" aria-hidden="true" />
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="fenceProcess">
        <div className="container fenceProcessGrid">
          <div className="fenceProcessPanel">
            <div className="fenceSectionHead alignLeft">
              <h2>How the Work Moves</h2>
              <p>The process stays organized so the finished fence matches the agreed scope.</p>
            </div>
          </div>

          <div className="fenceStepList">
            {processSteps.map((step, index) => (
              <div className="fenceStep" key={step}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <p>{step}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="fenceCta">
        <div className="fenceCtaInner">
          <h2>Ready to plan the fence line?</h2>
          <p>
            Send us the basics and we can review the site, discuss the goal, and define the right next step.
          </p>
          <button className="rBtn" onClick={() => navigate("/contact")}>
            REQUEST AN ESTIMATE
          </button>
        </div>
      </section>

      <Footer />
    </main>
  );
}
